import { Box, Button, HStack, Text } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Column, Row, useFilters, usePagination, useSortBy, useTable } from 'react-table';
import { Shield } from '~auth/Shield';
import { useAuth } from '~auth/useAuth';
import { useDeleteCustomer } from '~components/customers/mutations/DeleteCustomer';
import { EditIcon, TrashIcon } from '~components/ui/icons';
import { EmailNotificationIcon } from '~components/ui/icons/EmailNotificationIcon';
import { defaultPageSizes as pageSizes } from '~components/ui/PageSizeSelector';
import { SubscriptionTagV2 } from '~components/ui/SubscriptionTagV2';
import { generateTableCellComponent } from '~components/ui/TableCell';
import { NotificationStatus } from '~graphql/__generated__/types';
import { useAnalyticsReporter } from '~utils/analytics';
import { Feature, useFeatureFlag } from '~utils/features';
import { getCustomerSubscriptionTagVariant, useSubscriptionType } from '~utils/subscriptions';
import { CustomersTableCustomerFragment } from './__generated__/CustomersTable.graphql';
import { Columns } from './types';

const TableCell = generateTableCellComponent<CustomersTableCustomerFragment, ReactNode>();

const TableCellBold = generateTableCellComponent<CustomersTableCustomerFragment, ReactNode>({
  isBold: true,
});
interface CustomerActionsCellProps {
  row: Row<CustomersTableCustomerFragment>;
  onManageNotifications?: (customerId: string, customerName: string) => void;
}
function CustomerActionsCell({ row: { original: customer } }: CustomerActionsCellProps) {
  const navigate = useNavigate();
  const analytics = useAnalyticsReporter();

  const deleteCustomer = useDeleteCustomer({
    onCompleted: () => {
      analytics.track('customerDeleteComplete');
    },
  });

  const handleDelete = async () => {
    analytics.track('customerDeleteStart');
    await deleteCustomer.action.askConfirmation(customer);
  };

  const handleEdit = () => navigate(customer.id, { state: { canGoBack: true } });
  const handleManageNotificationsClick = () => {
    // if (onManageNotifications) onManageNotifications(customer.id, customer.name);
    navigate(`./emailNotification/${customer.id}`, { state: { canGoBack: true } });
  };
  const { hasWaveSubscription: showManageNotifications } = useSubscriptionType(customer);

  const iconColor = useMemo(() => {
    if (customer?.notification?.status === NotificationStatus.Enabled) {
      return '#1D85E8';
    } else if (customer?.notification?.status === NotificationStatus.Disabled) {
      return '#BCCCDC';
    } else {
      return '#BCCCDC';
    }
  }, [customer?.notification?.status]);
  return (
    <Shield requiredPermissions={[Permission.CustomerDelete, Permission.CustomerUpdate]}>
      <HStack spacing={2} align="center">
        <Box minWidth="190px">
          {showManageNotifications && (
            <Text
              as="span"
              color="blue.500"
              cursor="pointer"
              fontSize={{ base: 'sm', md: '14px' }}
              fontWeight="600"
              _hover={{ textDecoration: 'underline' }}
              onClick={handleManageNotificationsClick}
              whiteSpace="nowrap"
            >
              <HStack spacing={1} display="inline-flex">
                <EmailNotificationIcon width="32px" height="32px" color={iconColor} />
                <span>{t('manageNotifications')}</span>
              </HStack>
            </Text>
          )}
        </Box>
        <Button size="sm" variant="inline" colorScheme="gray" onClick={handleEdit}>
          <EditIcon color="gray.300" width="actionIconSize" height="actionIconSize" />
        </Button>
        <Button size="sm" variant="inline" colorScheme="gray" onClick={handleDelete}>
          <TrashIcon color="gray.300" width="actionIconSize" height="actionIconSize" />
        </Button>
        {deleteCustomer.action.confirmationNode}
      </HStack>
    </Shield>
  );
}

export function useCustomersTable(data: CustomersTableCustomerFragment[]) {
  const { verifyUserPermissions } = useAuth();

  const columns = useMemo<Array<Column<CustomersTableCustomerFragment>>>(
    () => [
      {
        id: Columns.Name,
        Header: t('customer'),
        accessor: (customer) => customer.name,
        Cell: TableCellBold,
        width: 'auto',
      },
      {
        id: Columns.Users,
        Header: t('users'),
        accessor: (customer) => customer.users.length,
        Cell: TableCell,
        width: '140px',
      },
      {
        id: Columns.Displays,
        Header: t('Displays'),
        accessor: 'displayCount',
        Cell: TableCell,
        width: '140px',
      },
      {
        id: Columns.Subscription,
        Header: t('subscription'),
        Cell: ({ row }: { row: Row<CustomersTableCustomerFragment> }) => {
          const variant = getCustomerSubscriptionTagVariant(
            row.original.waveSubscriptions,
            row.original.displayCount,
            row.original.waveSubscriptionUsage,
          );
          return <SubscriptionTagV2 variant={variant} />;
        },
        width: 'auto',
      },
      {
        id: Columns.Actions,
        Cell: CustomerActionsCell,
        width: '160px',
      },
    ],
    [],
  );

  const hiddenColumns = useHiddenColumns(verifyUserPermissions, useFeatureFlag);

  return useTable(
    {
      columns,
      data,
      initialState: {
        pageSize: pageSizes[0],
        sortBy: [{ id: Columns.Name }],
        filters: [{ id: Columns.Name, value: [] }],
        hiddenColumns,
      },
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetSelectedRows: false,
      autoResetPage: false,
    },
    useFilters,
    useSortBy,
    usePagination,
  );
}

function useHiddenColumns(
  verifyUserPermissions: (permissions: Permission[]) => boolean,
  useFeatureFlag: (feature: Feature) => { isEnabled: boolean },
): Columns[] {
  const { isEnabled: isSubscriptionsEnabled } = useFeatureFlag('subscriptions');

  const hiddenColumns: Columns[] = [];

  if (!verifyUserPermissions([Permission.FeatureCustomerScopedAccess])) {
    hiddenColumns.push(Columns.Users);
  }

  if (!isSubscriptionsEnabled) {
    hiddenColumns.push(Columns.Subscription);
  }

  return hiddenColumns;
}
